<section class="config-panel table-panel">
  <div class="panel-header table-header">
    <div class="table-title">
      <span class="ph-title">{{ title }}</span>
      <span *ngIf="uiConfig?.showRecordCount !== false">{{ rows.length }} {{ uiConfig?.recordCountLabel || 'records' }}</span>
    </div>
    <div class="table-header-actions" *ngIf="!(pendingChangeCount && uiConfig?.showSaveAllButton !== false)">
      <button type="button" class="btn-add" *ngIf="uiConfig?.showAddButton !== false && !isAdding" (click)="addRow.emit()">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
        </svg>
        {{ addButtonLabel }}
      </button>
      <button type="button" class="btn-save" *ngIf="uiConfig?.showSaveAllButton !== false" (click)="saveAll.emit()">
        {{ uiConfig?.saveAllLabel || 'Save' }}{{ pendingChangeCount ? ' (' + pendingChangeCount + ')' : '' }}
      </button>
    </div>
  </div>

  <!-- Only rendered while there are unsaved changes; removed entirely (not just
       hidden) once Discard/Save clears the count, so no space is left behind. -->
  <div class="table-toolbar" *ngIf="pendingChangeCount && uiConfig?.showSaveAllButton !== false">
    <span class="pending-note" role="status" aria-live="polite">
      <span class="pending-note-dot" aria-hidden="true"></span>
      {{ pendingChangeCount }} Pending Change{{ pendingChangeCount > 1 ? 's' : '' }}. Click Save to apply.
    </span>
    <span class="toolbar-divider" aria-hidden="true"></span>
    <div class="table-header-actions">
      <button type="button" class="btn-discard" (click)="discardChanges.emit()">Discard Changes</button>
      <button type="button" class="btn-add" *ngIf="uiConfig?.showAddButton !== false && !isAdding" (click)="addRow.emit()">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
        </svg>
        {{ addButtonLabel }}
      </button>
      <button type="button" class="btn-save" *ngIf="uiConfig?.showSaveAllButton !== false" (click)="saveAll.emit()">
        {{ uiConfig?.saveAllLabel || 'Save' }}{{ pendingChangeCount ? ' (' + pendingChangeCount + ')' : '' }}
      </button>
    </div>
  </div>

  <div class="doa-filter-row" *ngIf="quickFilters.length">
    <select
      class="custom-input doa-filter-select"
      *ngFor="let filter of quickFilters"
      [ngModel]="filterValues[filter.key] || ''"
      (ngModelChange)="filterChange.emit({ key: filter.key, value: $event })"
    >
      <option value="">{{ filter.label }}</option>
      <option *ngFor="let option of filter.options" [value]="option">{{ option }}</option>
    </select>

    <div class="input-with-icon doa-filter-search">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      <input
        type="text"
        class="custom-input doa-text doa-text-icon"
        placeholder="search"
        [ngModel]="filterValues['__search'] || ''"
        (ngModelChange)="filterChange.emit({ key: '__search', value: $event })"
      />
    </div>

    <button
      type="button"
      class="doa-filter-reset"
      title="Reset filters"
      aria-label="Reset filters"
      (click)="resetFilters.emit()"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M3 4h10.5M3 4l7 8v7l4-2v-5l1.2-1.4"></path>
        <circle cx="18" cy="6" r="4.5"></circle>
        <path d="m16.5 4.5 3 3m0-3-3 3"></path>
      </svg>
    </button>
  </div>

  <div class="table-container" [class.is-resizing]="resizingColumnKey" *ngIf="!loading && (rows.length || isAdding); else stateTpl">
    <table class="glass-table" [style.width.px]="resizedTableWidth">
      <colgroup>
        <col *ngFor="let column of columns" [style.width]="getColumnWidth(column)" />
        <col *ngIf="uiConfig?.showActionsColumn !== false" class="actions-column-width" />
      </colgroup>
      <thead>
        <tr>
          <th *ngFor="let column of columns" [attr.data-column-key]="column.key"
            [style.width]="getColumnWidth(column)"
            [style.min-width]="getColumnWidth(column)"
            [style.max-width]="getColumnWidth(column)">
            <span class="column-header-label">{{ column.label }}</span>
            <button
              type="button"
              class="column-resize-handle"
              [class.active]="resizingColumnKey === column.key"
              [attr.aria-label]="'Resize ' + column.label + ' column'"
              [title]="'Resize ' + column.label + ' column'"
              (pointerdown)="startColumnResize($event, column)"
              (click)="$event.stopPropagation()"
            >
              <span aria-hidden="true">|</span>
            </button>
          </th>
          <th *ngIf="uiConfig?.showActionsColumn !== false" class="col-actions sticky-col">{{ uiConfig?.actionsHeaderLabel || 'Actions' }}</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngIf="isAdding && draftRow" class="row-draft">
          <td *ngFor="let column of columns"
            [style.width]="getColumnWidth(column)"
            [style.min-width]="getColumnWidth(column)"
            [style.max-width]="getColumnWidth(column)">
            <ng-container *ngIf="column.editable !== false; else draftReadonly">
              <select
                *ngIf="hasDraftSelectOptions(column); else draftTextInput"
                class="custom-input table-input"
                [(ngModel)]="draftRow[column.key]"
                (click)="$event.stopPropagation()"
              >
                <option value="" disabled>{{ column.label }}</option>
                <option *ngFor="let option of getSelectOptions(column)" [value]="option">
                  {{ option }}
                </option>
              </select>
              <ng-template #draftTextInput>
                <input
                  type="text"
                  class="custom-input table-input"
                  [(ngModel)]="draftRow[column.key]"
                  [placeholder]="column.label"
                  (click)="$event.stopPropagation()"
                />
              </ng-template>
            </ng-container>
            <ng-template #draftReadonly>
              <span class="muted">-</span>
            </ng-template>
          </td>
          <td *ngIf="uiConfig?.showActionsColumn !== false" class="col-actions sticky-col">
            <div class="inline-actions">
              <button type="button" class="btn-inline-save" title="Save row" (click)="commitAdd.emit()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M20 6 9 17l-5-5"></path>
                </svg>
              </button>
              <button type="button" class="btn-inline-cancel" title="Cancel" (click)="cancelAdd.emit()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </td>
        </tr>

        <tr
          *ngFor="let row of rows; trackBy: trackById"
          [class.row-selected]="selectedRowId === row.id || editingIds.has(row.id)"
          [class.row-inactive]="!isRowActive(row)"
          [class.row-pending-delete]="isPendingDelete(row)"
          [class.row-pending]="isPendingRow(row) && !isPendingDelete(row)"
          (click)="rowClick.emit(row.id)"
        >
          <td
            *ngFor="let column of columns"
            [class.fw-bold]="column.key === columns[0].key"
            [class.meta-cell]="column.type === 'email'"
            [attr.title]="cellTooltip(row, column)"
            [style.width]="getColumnWidth(column)"
            [style.min-width]="getColumnWidth(column)"
            [style.max-width]="getColumnWidth(column)"
          >
            <ng-container *ngIf="editingIds.has(row.id) && column.editable !== false && uiConfig?.inlineEditing !== false; else readCell">
              <select
                *ngIf="hasEditSelectOptions(column); else editTextInput"
                class="custom-input table-input"
                [(ngModel)]="editValues.get(row.id)![column.key]"
                (click)="$event.stopPropagation()"
              >
                <option value="" disabled>{{ column.label }}</option>
                <option *ngFor="let option of getSelectOptions(column)" [value]="option">
                  {{ option }}
                </option>
              </select>
              <ng-template #editTextInput>
                <input
                  type="text"
                  class="custom-input table-input"
                  [(ngModel)]="editValues.get(row.id)![column.key]"
                  (click)="$event.stopPropagation()"
                />
              </ng-template>
            </ng-container>
            <ng-template #readCell>
              <span *ngIf="column.type === 'status'; else normalCell" class="status-badge" [attr.data-status]="displayValue(row, column)">
                {{ displayValue(row, column) }}
              </span>
              <ng-template #normalCell>{{ displayValue(row, column) }}</ng-template>
            </ng-template>
            <span
              *ngIf="isFirstColumn(column) && !editingIds.has(row.id) && pendingBadgeLabel(row) as badge"
              class="pending-pill"
              [attr.data-kind]="badge"
              title="Unsaved change — click Save to commit"
            >
              {{ badge }}
            </span>
          </td>
          <td *ngIf="uiConfig?.showActionsColumn !== false" class="col-actions sticky-col">
            <div class="fan-wrap">
              <div class="fan-rest">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="5" cy="12" r="1.7"></circle>
                  <circle cx="12" cy="12" r="1.7"></circle>
                  <circle cx="19" cy="12" r="1.7"></circle>
                </svg>
              </div>
              <button
                *ngIf="uiConfig?.showEditAction !== false"
                type="button"
                class="fan fan-edit"
                [class.editing]="editingIds.has(row.id)"
                [title]="editingIds.has(row.id) ? 'Cancel edit' : 'Edit'"
                (click)="$event.stopPropagation(); editToggle.emit(row)"
              >
                <svg *ngIf="!editingIds.has(row.id)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                </svg>
                <svg *ngIf="editingIds.has(row.id)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <button
                *ngIf="uiConfig?.showToggleAction !== false"
                type="button"
                class="fan fan-toggle"
                [class.on]="editingIds.has(row.id) ? editValues.get(row.id)?.active : row.active"
                title="Toggle active"
                (click)="$event.stopPropagation(); activeToggle.emit(row)"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1">
                  <path d="M12 3v9"></path>
                  <path d="M6.4 7.4a8 8 0 1 0 11.2 0"></path>
                </svg>
              </button>

            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <ng-template #stateTpl>
    <div class="table-state">
      <div class="loader" *ngIf="loading"></div>
      <div class="empty-state" *ngIf="!loading">
        <span>{{ uiConfig?.emptyStateMessage || 'No records found' }}</span>
      </div>
    </div>
  </ng-template>

</section>
